import { forwardRef } from 'react'
import ReactDatePicker from 'react-datepicker'
import cx from 'classnames'
import { isValid } from 'date-fns'

import { env } from 'common/config/env'
import { CalendarSquare as CalendarSquareIcon } from 'common/icons'

import 'react-datepicker/dist/react-datepicker.css'
import './DatePickerOverrides.css'

const toDateFnsFormat = (fmt: string) =>
  fmt.replace(/YYYY/g, 'yyyy').replace(/DD/g, 'dd').replace(/\bD\b/g, 'd')

const DATE_FORMAT = toDateFnsFormat(env.defaultDateFormat)

const inputClass =
  'w-full px-4 pr-10 bg-gold-40 h-10 rounded-sm font-normal text-base-700 text-sm outline-none border border-gold-400 hover:border-gold-500 focus:border-base-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed placeholder:text-gold-500'

const CalendarInput = forwardRef<HTMLInputElement, any>(
  ({ className: _cls, disabled, ...props }, ref) => (
    <div className="relative flex items-center w-full">
      <input ref={ref} {...props} disabled={disabled} className={inputClass} />
      <CalendarSquareIcon
        isDefaultColor={false}
        className={cx(
          'absolute right-3 pointer-events-none',
          disabled ? 'text-gold-300' : 'text-gold-700'
        )}
        size={4}
      />
    </div>
  )
)

export type DatePickerProps = {
  date?: Date | null
  onDateChange?: (date: Date | null) => void
  disabled?: boolean
  isOutsideRange?: (date: Date) => boolean
  isTodayVisible?: boolean
  showClearDate?: boolean
  numberOfMonths?: number
  autoFocus?: boolean
  onBlur?: () => void
  id?: string
  placeholder?: string
  'data-testid'?: string
  'aria-label'?: string | (() => string)
  ariaLabel?: string
  openDirection?: 'up' | 'down'
  appendToBody?: boolean
  // absorbed/ignored react-dates compat props
  verticalHeight?: number
  hideKeyboardShortcutsPanel?: boolean
  inputIconPosition?: string
  renderNavNextButton?: unknown
  renderNavPrevButton?: unknown
  customInputIcon?: unknown
  customCloseIcon?: unknown
  customArrowIcon?: unknown
  isError?: boolean
  renderMonthText?: unknown
  renderMonthElement?: unknown
  focused?: unknown
  onFocusChange?: unknown
  'data-cy'?: string
  showClearDates?: boolean
  reopenPickerOnClearDates?: boolean
  keepOpenOnDateSelect?: boolean
  anchorDirection?: unknown
  horizontalMargin?: unknown
  maxDate?: unknown
  displayFormat?: unknown
  startDateId?: unknown
  endDateId?: unknown
}

const DatePicker = ({
  date,
  onDateChange,
  disabled,
  isOutsideRange,
  isTodayVisible,
  showClearDate,
  numberOfMonths = 1,
  autoFocus = false,
  onBlur,
  id,
  placeholder,
  'data-testid': dataTestId,
  ariaLabel,
  openDirection,
  // absorbed/ignored:
  verticalHeight: _v,
  hideKeyboardShortcutsPanel: _h,
  inputIconPosition: _i,
  renderNavNextButton: _rnb,
  renderNavPrevButton: _rpb,
  customInputIcon: _ci,
  customCloseIcon: _cc,
  customArrowIcon: _ca,
  isError: _err,
  renderMonthText: _rmt,
  renderMonthElement: _rme,
  focused: _f,
  onFocusChange: _ofc,
  'data-cy': _cy,
  showClearDates: _scd,
  reopenPickerOnClearDates: _rp,
  keepOpenOnDateSelect: _ko,
  anchorDirection: _ad,
  horizontalMargin: _hm,
  maxDate: _max,
  displayFormat: _df,
  startDateId: _sid,
  endDateId: _eid,
  ...rest
}: DatePickerProps & Record<string, unknown>) => {
  const ariaLabelStr = typeof ariaLabel === 'string' ? ariaLabel : undefined

  const safeDate = date != null && isValid(date) ? date : null

  return (
    <ReactDatePicker
      id={id}
      selected={safeDate}
      onChange={(d) => onDateChange?.(d)}
      dateFormat={DATE_FORMAT}
      disabled={disabled}
      isClearable={showClearDate}
      placeholderText={placeholder}
      filterDate={isOutsideRange ? (d: Date) => !isOutsideRange(d) : undefined}
      todayButton={isTodayVisible ? 'Today' : undefined}
      monthsShown={numberOfMonths}
      autoFocus={autoFocus}
      onBlur={onBlur}
      popperPlacement={openDirection === 'up' ? 'top-start' : 'bottom-start'}
      data-testid={dataTestId}
      aria-label={ariaLabelStr}
      wrapperClassName="w-full"
      customInput={<CalendarInput disabled={disabled} />}
      previousMonthButtonLabel="←"
      nextMonthButtonLabel="→"
      {...(rest as any)}
    />
  )
}

export default DatePicker
